import type { UserProfileResponse } from '@models';

/**
 * Utility function to check if a user profile is incomplete.
 * A profile is considered incomplete if all of these fields are empty:
 * - interests: null or empty array
 * - professional_background: empty string or undefined
 * - can_help_with: empty string or undefined
 */
export const isProfileIncomplete = (
  userProfile: UserProfileResponse | null | undefined,
): boolean => {
  if (!userProfile) return true;

  const { interests, professional_background, can_help_with } = userProfile;
  const hasNoInterests = !interests || interests.length === 0;
  const hasNoProfessionalBackground =
    !professional_background || professional_background.trim() === '';
  const hasNoCanHelpWith = !can_help_with || can_help_with.trim() === '';

  return hasNoInterests && hasNoProfessionalBackground && hasNoCanHelpWith;
};

/**
 * Utility function to check if a user profile can be matched as a helper.
 * The helper feed only indexes profiles that declare a help direction, so a
 * profile is matchable when EITHER of these is present:
 * - can_help_with: a non-empty string (after trimming)
 * - fields_of_expertise: a non-empty array (null counts as empty)
 */
export const isHelperProfileMatchable = (
  userProfile: UserProfileResponse | null | undefined,
): boolean => {
  if (!userProfile) return false;

  const { can_help_with, fields_of_expertise } = userProfile;
  const hasCanHelpWith = !!can_help_with?.trim();
  const hasFieldsOfExpertise = !!fields_of_expertise && fields_of_expertise.length > 0;

  return hasCanHelpWith || hasFieldsOfExpertise;
};
